import { zodResolver } from "@hookform/resolvers/zod";
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import LockRounded from "@mui/icons-material/LockRounded";
import type {
  UserProfileResponse,
  UserProfileUpdate,
} from "@bw/shared/types/generated";
import { Controller, useForm } from "react-hook-form";
import {
  editProfileSchema,
  GENDER_OPTIONS,
  VISIBILITY_OPTIONS,
  type EditProfileValues,
} from "../schemas";
import { ProfilePhotoUploadField } from "../../../common/components/ProfilePhotoUploadField";
import { SegmentedRadioGroup } from "./SegmentedRadioGroup";
import {
  PlayFormatChips,
  SkillLevelPicker,
} from "../../../common/components/PlayStyleFields";
import { visuallyHidden } from "../../../common/visuallyHidden";

function LockedRow({ label, value }: { label: string; value: string }) {
  return (
    <Stack direction="row" spacing={1.5} sx={{ alignItems: "center", py: 0.5 }}>
      <LockRounded fontSize="small" color="disabled" aria-hidden="true" />
      <Typography sx={{ flex: 1, fontWeight: 600 }}>{label}</Typography>
      <Typography color="text.secondary">
        {value}
        <Box component="span" sx={visuallyHidden}>
          , locked
        </Box>
      </Typography>
    </Stack>
  );
}

type GenderValue = (typeof GENDER_OPTIONS)[number]["value"];
type VisibilityValue = (typeof VISIBILITY_OPTIONS)[number]["value"];

interface ProfileEditFormProps {
  profile: UserProfileResponse;
  onSubmit: (body: UserProfileUpdate) => void;
  submitting?: boolean;
  onCancel?: () => void;
}

export function ProfileEditForm({
  profile,
  onSubmit,
  submitting = false,
  onCancel,
}: ProfileEditFormProps) {
  const seededVisibility =
    profile.visibility === "private" ? "private" : "public";
  const defaults: EditProfileValues = {
    display_name: profile.display_name,
    gender: profile.gender ?? undefined,
    play_formats: profile.play_formats ?? [],
    skill_level: profile.skill_level ?? undefined,
    avatar: profile.avatar ?? "",
    bio: profile.bio ?? "",
    visibility: seededVisibility,
    profile_shows_play_details: profile.profile_shows_play_details === true,
  };
  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors },
  } = useForm<EditProfileValues>({
    resolver: zodResolver(editProfileSchema),
    mode: "onChange",
    defaultValues: defaults,
  });
  const avatarValue = watch("avatar");

  const submit = handleSubmit((values) => {
    const body: UserProfileUpdate = {};
    const nextName = values.display_name.trim();
    if (nextName !== profile.display_name) body.display_name = nextName;
    // Gender is set once (sign-up, or here for an older profile); then it locks.
    if (!profile.gender && values.gender) body.gender = values.gender;
    const nextFormats = values.play_formats ?? [];
    const savedFormats = profile.play_formats ?? [];
    if (nextFormats.join() !== savedFormats.join())
      body.play_formats = nextFormats;
    if (values.skill_level && values.skill_level !== profile.skill_level)
      body.skill_level = values.skill_level;
    const nextAvatar = values.avatar ?? "";
    if (nextAvatar !== (profile.avatar ?? "")) body.avatar = nextAvatar;
    const nextBio = values.bio ?? "";
    if (nextBio !== (profile.bio ?? "")) body.bio = nextBio;
    const nextVisibility = values.visibility ?? seededVisibility;
    if (nextVisibility !== seededVisibility) body.visibility = nextVisibility;
    const nextShows = values.profile_shows_play_details === true;
    if (nextShows !== (profile.profile_shows_play_details === true))
      body.profile_shows_play_details = nextShows;
    onSubmit(body);
  });

  return (
    <Paper
      component="form"
      variant="outlined"
      onSubmit={submit}
      noValidate
      sx={{ p: 2.5 }}
    >
      <Stack spacing={2.5}>
        <TextField
          label="Display name"
          autoComplete="name"
          required
          error={Boolean(errors.display_name)}
          helperText={errors.display_name?.message}
          {...register("display_name")}
        />

        <Controller
          control={control}
          name="play_formats"
          render={({ field }) => (
            <PlayFormatChips
              id="edit-formats"
              value={field.value}
              onChange={field.onChange}
            />
          )}
        />
        <Controller
          control={control}
          name="skill_level"
          render={({ field }) => (
            <SkillLevelPicker
              id="edit-level"
              value={field.value}
              onChange={field.onChange}
            />
          )}
        />

        <FormControl component="fieldset">
          <FormLabel component="legend">Eligibility</FormLabel>
          {profile.gender ? (
            <LockedRow
              label="Gender"
              value={
                GENDER_OPTIONS.find((option) => option.value === profile.gender)
                  ?.label ?? ""
              }
            />
          ) : (
            <Stack spacing={1}>
              <Typography id="edit-gender-label" sx={{ fontWeight: 600 }}>
                Gender
              </Typography>
              <Controller
                control={control}
                name="gender"
                render={({ field }) => (
                  <SegmentedRadioGroup<GenderValue>
                    ariaLabel="Gender for competition"
                    options={GENDER_OPTIONS}
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
            </Stack>
          )}
          <LockedRow label="Date of birth" value="Never shown" />
          <FormHelperText sx={{ mx: 0 }}>
            {profile.gender
              ? "Used to place you in age and gender events. These can't be changed here; contact support if something is wrong."
              : "Used to place you in men's, women's and mixed events. Once saved, gender can't be changed here."}
          </FormHelperText>
        </FormControl>

        <FormControl component="fieldset">
          <FormLabel component="legend">Who can see you</FormLabel>
          <Controller
            control={control}
            name="visibility"
            render={({ field }) => (
              <SegmentedRadioGroup<VisibilityValue>
                ariaLabel="Profile visibility"
                options={VISIBILITY_OPTIONS}
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
        </FormControl>

        <Controller
          control={control}
          name="profile_shows_play_details"
          render={({ field }) => (
            <FormControl>
              <FormControlLabel
                control={
                  <Switch
                    checked={field.value === true}
                    onChange={(event) => field.onChange(event.target.checked)}
                    disabled={profile.is_minor !== false}
                  />
                }
                label="Show where I play and my communities"
              />
              <FormHelperText sx={{ mx: 0 }}>
                {profile.is_minor !== false
                  ? "Available from age 18."
                  : "Lists your home area, favourite places and public communities while your profile is public."}
              </FormHelperText>
            </FormControl>
          )}
        />

        <input type="hidden" {...register("avatar")} />
        <ProfilePhotoUploadField
          value={avatarValue}
          onChange={(url) =>
            setValue("avatar", url, {
              shouldValidate: true,
              shouldDirty: true,
            })
          }
          disabled={submitting}
          validationMessage={errors.avatar?.message}
        />

        <TextField
          label="About you (optional)"
          multiline
          minRows={3}
          placeholder="A sentence about your game"
          {...register("bio")}
        />

        <Stack direction="row" spacing={1} sx={{ justifyContent: "flex-end" }}>
          {onCancel ? (
            <Button
              type="button"
              variant="outlined"
              size="small"
              onClick={onCancel}
              disabled={submitting}
            >
              Cancel
            </Button>
          ) : null}
          <Button
            type="submit"
            variant="contained"
            size="small"
            disabled={submitting}
          >
            Save changes
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
}
